import { useEffect, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useOffline } from "@/lib/net";
import { formatBalance, formatIsoDay, formatTRY } from "@/lib/format";
import { BANKS, Field, METHOD_ACCOUNT, METHODS, sel, todayIso, useAccounts, AccLabel, accColor } from "@/lib/finance";
import SearchPicker, { fetchCustomers } from "@/components/sales/SearchPicker";

function fifoPreview(items, amount) {
  let left = amount;
  return items.map((i) => {
    const take = Math.max(0, Math.min(left, i.remaining));
    left = Math.round((left - take) * 100) / 100;
    return { ...i, take };
  });
}

function OpenItems({ data, amount, fifo }) {
  if (!data) return null;
  if (!data.items.length) return <div className="text-[12px] text-slate-400" data-testid="pay-no-open-items">Açık fatura/borç kalemi yok; tutar cari hesaba alacak olarak işlenir.</div>;
  const rows = fifo ? fifoPreview(data.items, amount) : data.items;
  return (
    <div className="border border-slate-200 rounded max-h-44 overflow-y-auto" data-testid="pay-open-items">
      <table className="erp-table text-[12px]">
        <thead><tr><th>Belge</th><th>Vade</th><th className="text-right">Kalan</th>{fifo && <th className="text-right">Kapanacak</th>}</tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} className={fifo && r.take > 0 ? "bg-emerald-50/60" : ""}>
            <td className="font-mono">{r.doc_no}</td>
            <td className={`font-mono ${r.days_overdue > 0 ? "text-rose-600" : ""}`}>{formatIsoDay(r.due_date)}</td>
            <td className="text-right font-mono">{formatTRY(r.remaining)}</td>
            {fifo && <td className="text-right font-mono font-semibold text-emerald-700">{r.take > 0 ? formatTRY(r.take) : ""}</td>}
          </tr>))}</tbody>
      </table>
    </div>
  );
}

function InstrumentFields({ method, v, set }) {
  const up = (k) => (e) => set({ ...v, [k]: e.target.value });
  return (
    <div className="col-span-2 grid grid-cols-2 md:grid-cols-3 gap-3 p-3 rounded border border-blue-100 bg-blue-50/40" data-testid="pay-instrument-fields">
      <Field label="Seri No *"><Input value={v.serial_no || ""} onChange={up("serial_no")} data-testid="pay-instr-serial" /></Field>
      <Field label="Vade Tarihi *"><Input type="date" value={v.due_date || ""} onChange={up("due_date")} data-testid="pay-instr-due" /></Field>
      {method === "cek" && (
        <Field label="Banka *">
          <select className={`${sel} w-full`} value={v.bank_name || ""} onChange={up("bank_name")} data-testid="pay-instr-bank">
            <option value="">Seçin</option>{BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </Field>
      )}
      {method === "cek" && <Field label="Şube"><Input value={v.branch || ""} onChange={up("branch")} data-testid="pay-instr-branch" /></Field>}
      <Field label="Keşideci / Borçlu" className={method === "cek" ? "md:col-span-2" : ""}><Input value={v.drawer_name || ""} placeholder="Boşsa cari ünvanı" onChange={up("drawer_name")} data-testid="pay-instr-drawer" /></Field>
    </div>
  );
}

function useCustomerData(customer) {
  const [open, setOpen] = useState(null);
  const [promises, setPromises] = useState([]);
  useEffect(() => {
    setOpen(null); setPromises([]);
    if (!customer) return;
    api.get(`/customers/${customer.id}/open-invoices`).then(({ data }) => setOpen(data)).catch(() => setOpen({ items: [] }));
    api.get("/odeme-sozleri", { params: { customer_id: customer.id, status: "bekliyor,kismen,gecikti" } }).then(({ data }) => setPromises(data.items)).catch(() => {});
  }, [customer]);
  return { open, promises };
}

export default function CollectionDialog({ customer: preset, promise, onClose, onDone, initialMethod, initialAmount, initialNote, initialDate, initialAccountId }) {
  const [customer, setCustomer] = useState(preset || null);
  const off = useOffline();
  const [method, setMethod] = useState(initialMethod || "havale");
  const [amount, setAmount] = useState(promise ? String(promise.amount) : initialAmount ? String(initialAmount) : "");
  const [date, setDate] = useState(initialDate || todayIso());
  const [accountId, setAccountId] = useState("");
  const [fifo, setFifo] = useState(true);
  const [promiseId, setPromiseId] = useState(promise?.id || "");
  const [note, setNote] = useState(initialNote || "");
  const [instr, setInstr] = useState({});
  const [busy, setBusy] = useState(false);
  const [idem] = useState(() => crypto.randomUUID());
  const accounts = useAccounts();
  const { open, promises } = useCustomerData(customer);
  const isInstr = method === "cek" || method === "senet";
  const options = useMemo(() => accounts.filter((a) => a.type === METHOD_ACCOUNT[method]), [accounts, method]);
  useEffect(() => { if (!isInstr) setAccountId(options.some((a) => a.id === initialAccountId) ? initialAccountId : (options.find((a) => a.defaults.includes(method)) || options[0])?.id || ""); }, [options, method, isInstr, initialAccountId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { customer_id: customer.id, method, amount: Number(amount), date, match_fifo: fifo, note: note || null, promise_id: promiseId || null,
        account_id: isInstr ? null : accountId || null, instrument: isInstr ? instr : null };
      const { data } = await api.post("/tahsilatlar", body, { headers: { "Idempotency-Key": idem } });
      toast.success(`${data.payment_no} kaydedildi · ${formatTRY(data.amount)}${data.instrument_no ? ` · ${data.instrument_no} portföye alındı` : ""}`);
      onDone(data);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="collection-dialog">
        <DialogHeader><DialogTitle>Yeni Tahsilat</DialogTitle></DialogHeader>
        {!customer ? (
          <SearchPicker testid="pay-customer-search" placeholder="Cari ara: ünvan, kod veya telefon" fetcher={fetchCustomers} onPick={setCustomer}
            render={(c) => <div><div className="text-[13px] font-medium">{c.title}</div><div className="text-[11px] font-mono text-slate-400">{c.code} · {formatBalance(c.balance)}</div></div>} />
        ) : (
          <div className="flex items-center justify-between rounded border border-slate-200 px-3 py-2 bg-slate-50" data-testid="pay-selected-customer">
            <div><div className="font-semibold text-slate-900 text-sm">{customer.title}</div><div className="text-[11px] font-mono text-slate-500">{customer.code} · Bakiye {formatBalance(customer.balance)}</div></div>
            {!preset && <button type="button" onClick={() => setCustomer(null)} className="text-slate-400 hover:text-rose-600" data-testid="pay-clear-customer"><X className="h-4 w-4" /></button>}
          </div>
        )}
        {customer && (
          <form onSubmit={submit} className="grid grid-cols-2 gap-3">
            <div className="col-span-2 grid grid-cols-5 gap-1">
              {Object.entries(METHODS).map(([k, l]) => (
                <button key={k} type="button" onClick={() => setMethod(k)} data-testid={`pay-method-${k}`}
                  className={`h-9 text-[12px] rounded border transition-colors ${method === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-blue-600"}`}>{l}</button>
              ))}
            </div>
            <Field label="Tutar (₺)"><Input type="number" step="0.01" min="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} className="font-mono" data-testid="pay-amount" /></Field>
            <Field label="Tahsilat Tarihi"><Input type="date" required value={date} onChange={(e) => setDate(e.target.value)} data-testid="pay-date" /></Field>
            {!isInstr && (
              <Field label={<AccLabel text="Hesap" color={accColor(options, accountId)} />} className="col-span-2">
                <select className={`${sel} w-full`} value={accountId} onChange={(e) => setAccountId(e.target.value)} data-testid="pay-account">
                  {options.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </Field>
            )}
            {isInstr && <InstrumentFields method={method} v={instr} set={setInstr} />}
            {promises.length > 0 && (
              <Field label="Ödeme sözüne bağla" className="col-span-2">
                <select className={`${sel} w-full`} value={promiseId} onChange={(e) => setPromiseId(e.target.value)} data-testid="pay-promise">
                  <option value="">Bağlama</option>
                  {promises.map((p) => <option key={p.id} value={p.id}>{p.promise_no} · {formatIsoDay(p.promise_date)} · {formatTRY(p.amount)}</option>)}
                </select>
              </Field>
            )}
            <div className="col-span-2 space-y-2">
              <label className="flex items-center gap-2 text-[13px] text-slate-700"><input type="checkbox" checked={fifo} onChange={(e) => setFifo(e.target.checked)} data-testid="pay-fifo" />Açık faturalarla FIFO eşleştir (en eski vade önce)</label>
              <OpenItems data={open} amount={Number(amount) || 0} fifo={fifo} />
            </div>
            <Field label="Not" className="col-span-2"><Input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} data-testid="pay-note" /></Field>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={onClose} data-testid="pay-cancel">Vazgeç</Button>
              <Button type="submit" disabled={busy || off || !(Number(amount) > 0)} className="bg-blue-600 hover:bg-blue-700" data-testid="pay-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Tahsilatı Kaydet</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
